import { DialogActions, DialogTitle, IconButton } from "@mui/material";
import {
  BarthelAssessment,
  DeepNullableObj,
  EarlyRehabAssessment,
  GKVMedicalFormData,
  SingerAssessment,
} from "core/types";
import RSButton from "ds_legacy/components/RSButton";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_20,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_SEMI_BOLD,
  LINE_HEIGHT_24,
} from "ds_legacy/materials/typography";
import { useScrollToElement } from "dsl/hooks/index";
import { XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ObservableValue } from "react-forms-state/src/Context";
import { Observable } from "rxjs";
import { useTranslations } from "translations";

type ScoreForms =
  | BarthelAssessment["form"]
  | EarlyRehabAssessment["form"]
  | SingerAssessment["form"];

export function calculateScore(form?: ScoreForms): number {
  if (!form) return 0;

  return Object.values(form)
    .filter((value) => typeof value === "number")
    .map((value) => +value)
    .reduce((currentValue, newValue) => currentValue + newValue, 0);
}

export function useGetFormValues<IsGkvFrom extends boolean = false>(
  valueChangeObs: Observable<ObservableValue> | undefined,
  isGKVForm?: boolean,
) {
  const [formValue, setFormValue] =
    useState<
      IsGkvFrom extends true
        ? GKVMedicalFormData
        :
            | SingerAssessment["form"]
            | EarlyRehabAssessment["form"]
            | BarthelAssessment["form"]
    >();

  useEffect(() => {
    if (!valueChangeObs) return;

    const obs = valueChangeObs.subscribe((e) => {
      // Create a new object to ensure immutability

      const currentValue = isGKVForm ? e.value : e.value?.form;
      const form = { ...(currentValue ?? {}) };
      Object.keys(form).forEach((key: string) => {
        if (typeof form[key] === "string") {
          form[key] = +form[key];
        }
      });
      setFormValue(form);
    });

    return () => obs.unsubscribe();
  }, [valueChangeObs, setFormValue]);

  return formValue;
}

export function useScrollToError(
  valueChangeObs: Observable<ObservableValue> | undefined,
  fieldNames: string[],
) {
  const { ref, scrollToElement } = useScrollToElement("instant", {
    block: "start",
  });

  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  useEffect(() => {
    valueChangeObs?.subscribe(({ validation }) => {
      if (
        typeof validation === "object" &&
        typeof validation.infos === "object"
      ) {
        if (validation.infos.date) {
          setScrollTarget("date");
        } else if (validation.infos.form) {
          const invalidKeys = Object.keys(validation.infos.form);
          for (const name of fieldNames) {
            if (invalidKeys.includes(name)) {
              setScrollTarget(name);
              return;
            }
          }
        }
      }
    });
  }, [valueChangeObs]);

  useEffect(() => {
    if (scrollTarget) {
      // scroll once and reset
      scrollToElement();
      setScrollTarget(null);
    }
  }, [scrollTarget]);

  const getScrollRef = (name: string) =>
    scrollTarget === name ? ref : undefined;

  return { getScrollRef };
}

export function DialogHeader({
  onCancel,
  title,
}: {
  onCancel?: () => void;
  title: string;
}) {
  const translations = useTranslations();
  return (
    <div
      data-testid="independence-dialog-header"
      style={{ display: "flex", justifyContent: "space-between" }}
    >
      <DialogTitle sx={{ fontWeight: FONT_WEIGHT_BOLD }}>{title}</DialogTitle>
      <IconButton
        aria-label={translations.actions.close}
        data-testid="close-modal"
        size="medium"
        sx={{
          boxSizing: "border-box",
          height: dp(44),
          margin: margin(1),
          width: dp(44),
        }}
        onClick={onCancel}
      >
        <XIcon size={16} />
      </IconButton>
    </div>
  );
}

export function TotalScore({ score }: { score: number }) {
  const translations = useTranslations();

  return (
    <Body
      as="p"
      data-testid="total-score"
      fontSize={FONT_SIZE_20}
      fontWeight={FONT_WEIGHT_SEMI_BOLD}
      lineHeight={LINE_HEIGHT_24}
      margin={0}
      style={{ display: "flex", gap: padding(1) }}
    >
      <span>
        {
          translations.patient.medicalDiagnosis.singerPatientProfile
            .scoreTotalScore
        }
      </span>
      <span>{score}</span>
    </Body>
  );
}

export const buttonIdMap = {
  barthel: {
    cancel: "barthel-form-cancel",
    reset: "barthel-form-reset",
    save: "barthel-form-save",
  },
  early_rehab: {
    cancel: "early_rehab-form-cancel",
    reset: "early_rehab-form-reset",
    save: "early_rehab-form-save",
  },
  singer: {
    cancel: "singer-form-cancel",
    reset: "singer-form-reset",
    save: "singer-form-save",
  },
} as const;

export function AssessmentActions({
  defaultValues,
  onCancel,
  onChange,
  submit,
  variant,
}: {
  defaultValues: DeepNullableObj<
    Omit<SingerAssessment | EarlyRehabAssessment | BarthelAssessment, "score">
  >;
  onCancel?: () => void;
  onChange?: (value: any, statePath: string, validation: any) => void;
  submit: () => void;
  variant: "early_rehab" | "singer" | "barthel";
}) {
  const translations = useTranslations();

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  return (
    <DialogActions
      sx={{
        justifyContent: "space-between",
        padding: padding(0, 3, 2.5, 3),
      }}
    >
      <RSButton
        color="primary"
        id={buttonIdMap[variant].reset}
        loading="na"
        style={{ margin: 0 }}
        type="reset"
        variant="outlined"
        onClick={() => onChange?.(defaultValues, "", true)}
      >
        {
          translations.patient.medicalDiagnosis.singerPatientProfile
            .buttonClearContent
        }
      </RSButton>
      <HorizontalLayout
        boxSizing="border-box"
        gap={padding(1)}
        overflow="visible"
      >
        <RSButton
          color="primary"
          id={buttonIdMap[variant].cancel}
          loading="na"
          style={{ margin: 0 }}
          variant="outlined"
          onClick={handleCancel}
        >
          {translations.actions.cancel}
        </RSButton>
        <div data-testid="submit">
          <RSButton
            color="primary"
            id={buttonIdMap[variant].save}
            loading="na"
            style={{ margin: 0 }}
            type="submit"
            variant="contained"
            onClick={submit}
          >
            {translations.actions.save}
          </RSButton>
        </div>
      </HorizontalLayout>
    </DialogActions>
  );
}
